/**
 * Read/write access to `program_closures` — the BHCS closure calendar.
 *
 * The read here is deliberately the *only* thing that talks to the table on the parent side.
 * Its rows are handed to `primeClosures`, after which every consumer keeps using the
 * synchronous accessors in `config/blackoutDates` exactly as before. See that file for why
 * the calendar must never become an async lookup in the Flex billing path.
 */
import { supabase } from '../lib/supabase';
import type { BlackoutDate, BlackoutKind } from '../config/blackoutDates';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';

/** A closure row as stored. `date` is a DATE column — no timezone conversion. */
export interface ClosureRow {
  date: string;
  label: string;
  kind: BlackoutKind;
  /** Whose calendar. Part of the primary key, alongside `date`. */
  brand_key: BrandKey;
  updated_by?: string | null;
  updated_at?: string | null;
}

/**
 * Every closure, oldest first.
 *
 * Unbounded on purpose: the whole calendar is a few dozen rows a year, and the consumer is a
 * lookup table that must be complete to be correct — a paged read that silently stopped at
 * 1000 would drop closures off the end of the school year.
 */
export async function fetchClosures(): Promise<BlackoutDate[]> {
  const { data, error } = await supabase
    .from('program_closures')
    .select('date, label, kind, brand_key')
    .order('date');
  if (error) throw new Error(error.message);
  return (data ?? []) as BlackoutDate[];
}

/**
 * Admin-only: create or replace one day. RLS rejects a non-admin.
 *
 * `onConflict` names the whole primary key, `(brand_key, date)`. It named `date` alone
 * before brands, which stopped matching a constraint the moment the key moved — so this call
 * and the key change cannot ship independently of each other. See the patch header.
 */
export async function upsertClosure(row: BlackoutDate): Promise<void> {
  const { error } = await supabase
    .from('program_closures')
    .upsert(
      {
        date: row.date,
        label: row.label,
        kind: row.kind,
        brand_key: row.brand_key ?? DEFAULT_BRAND_KEY,
      },
      { onConflict: 'brand_key,date' },
    );
  if (error) throw new Error(error.message);
}

/**
 * Admin-only: remove one day, so it becomes an ordinary running day again.
 *
 * The brand predicate is load-bearing, not decorative: without it this deletes **every**
 * brand's row for that date, so closing the BHCS calendar on a day would silently reopen
 * Kaleido's — or the reverse.
 */
export async function deleteClosure(
  date: string,
  brand: BrandKey = DEFAULT_BRAND_KEY,
): Promise<void> {
  const { error } = await supabase
    .from('program_closures')
    .delete()
    .eq('date', date)
    .eq('brand_key', brand);
  if (error) throw new Error(error.message);
}

/** A program that keeps at least one no-class day of its own. */
export interface ProgramExcludedDates {
  id: string;
  name: string;
  excluded_dates: string[];
}

/**
 * Programs carrying their own `excluded_dates`, for the School Calendar to show alongside
 * the school-wide days.
 *
 * Read-only there on purpose. These belong to a program, not to the calendar, and editing
 * them from two places is how the two would drift; the calendar links back to Admin →
 * Programs instead. Active programs only — a finished term's vendor day off is history and
 * would only clutter the month it fell in.
 */
export async function fetchProgramExcludedDates(): Promise<ProgramExcludedDates[]> {
  const { data, error } = await supabase
    .from('programs')
    .select('id, name, excluded_dates')
    .eq('active', true)
    .not('excluded_dates', 'is', null)
    .order('name');
  if (error) throw new Error(error.message);
  return ((data ?? []) as ProgramExcludedDates[]).filter(p => (p.excluded_dates?.length ?? 0) > 0);
}
